import RSDK from '@sr/functions/rsdk/src';
import { Resume } from '@sr/types';
import equal from 'fast-deep-equal/es6/react';
import React from 'react';
import { Box, Flex, MarkedInput, Text } from '../../ui';
import API from '../../utils/api';
import PlainText from '../tiptap-text/PlainText';

type Props = {
  resume: Resume;
  isSetup?: boolean;
};

const AboutForm = React.memo<Props>(function AboutForm({
  resume,
  isSetup,
}: Props) {
  function handleChange(
    e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const path = `${resume.id}/about/${e.currentTarget.name}`;
    API.resume.setProperty({
      path,
      value: e.currentTarget.value,
    });
  }

  function handleSummaryChange(value: string) {
    API.resume.setProperty({
      path: `${resume.id}/about/summary`,
      value,
    });
  }

  return (
    <Box mb={7} id="aboutid" sx={{ position: 'relative' }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        {isSetup !== true && (
          <>
            <Text variant="baseEmphasis" py={5}>
              About
            </Text>
            <Flex>
              <MarkedInput
                placeholder="First Name"
                value={resume.about.firstName}
                name="firstName"
                onChange={handleChange}
                srId={RSDK.getAboutPropSrId('firstName')}
                containerSx={{ width: '100%', mr: 1 }}
              />
              <MarkedInput
                placeholder="Last Name"
                value={resume.about.lastName}
                name="lastName"
                onChange={handleChange}
                srId={RSDK.getAboutPropSrId('lastName')}
                containerSx={{ width: '100%' }}
              />
            </Flex>
            <MarkedInput
              placeholder="Your Title"
              value={resume.about.title}
              name="title"
              onChange={handleChange}
              srId={RSDK.getAboutPropSrId('title')}
            />
            <PlainText
              content={resume.about.summary}
              placeholder="Summary"
              srId={RSDK.getAboutPropSrId('summary')}
              updateContent={handleSummaryChange}
            />
          </>
        )}
        <Flex>
          <MarkedInput
            placeholder="LinkedIn Profile URL"
            value={resume.about.website}
            name="website"
            onChange={handleChange}
            type="text"
            inputMode="url"
            srId={RSDK.getAboutPropSrId('website')}
            containerSx={{ width: '100%', mr: 1 }}
          />
          <MarkedInput
            placeholder="Website, GitHub, etc"
            value={resume.about.website2 || ''}
            name="website2"
            onChange={handleChange}
            type="text"
            inputMode="url"
            srId={RSDK.getAboutPropSrId('website2')}
            containerSx={{ width: '100%' }}
          />
        </Flex>
        <Flex>
          <MarkedInput
            placeholder="City"
            value={resume.about.city}
            name="city"
            onChange={handleChange}
            srId={RSDK.getAboutPropSrId('city')}
            containerSx={{ width: '100%', mr: 1 }}
          />
          <MarkedInput
            placeholder="State or Country"
            value={resume.about.state}
            name="state"
            onChange={handleChange}
            srId={RSDK.getAboutPropSrId('state')}
            containerSx={{ width: '100%' }}
          />
        </Flex>
        <Flex>
          <MarkedInput
            placeholder="Phone"
            value={resume.about.phone}
            name="phone"
            onChange={handleChange}
            type="phone"
            srId={RSDK.getAboutPropSrId('phone')}
            containerSx={{ width: '100%', mr: 1 }}
          />
          <MarkedInput
            placeholder="Email"
            value={resume.about.email}
            name="email"
            onChange={handleChange}
            type="email"
            srId={RSDK.getAboutPropSrId('email')}
            containerSx={{ width: '100%' }}
          />
        </Flex>
      </form>
    </Box>
  );
},
areEqual);

// Note: this is a fairly dangerous check because it could skip renders, but is expensive to render.
// Only resume.about is the only data that is used the could change (non-id).
function areEqual(prevProps: Props, nextProps: Props): boolean {
  return equal(prevProps.resume.about, nextProps.resume.about);
}

export default AboutForm;
